// src/landings/components/ErrorBoundary.tsx

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen relative">
          {/* Background */}
          <div className="fixed inset-0 w-full h-full overflow-hidden z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fff5eb] to-[#f9f6f4]"></div>
            
            {/* Grid pattern background */}
            <svg className="absolute inset-0 w-full h-full opacity-[0.07]" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M0 10 L40 10" stroke="#946e5d" strokeWidth="0.5" fill="none" />
                  <path d="M0 20 L40 20" stroke="#946e5d" strokeWidth="0.5" fill="none" />
                  <path d="M0 30 L40 30" stroke="#946e5d" strokeWidth="0.5" fill="none" />
                  <path d="M10 0 L10 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
                  <path d="M20 0 L20 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
                  <path d="M30 0 L30 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          <div className="relative z-10">
            <Header />
            
            <main className="min-h-[70vh] flex items-center justify-center p-4">
              <div className="max-w-md w-full text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#fff5eb] flex items-center justify-center text-[#ff8c42]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                
                <h1 className="text-3xl font-bold mb-4 text-[#694e42]">
                  Something went wrong
                </h1>
                
                <p className="text-[#8a7266] mb-8">
                  We encountered an unexpected error. Please try refreshing the page or navigate back to the homepage.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-3 bg-[#ff8c42] text-white rounded-md font-medium"
                  >
                    Refresh Page
                  </button>
                  
                  <Link
                    to="/"
                    className="px-6 py-3 border border-[#694e42] text-[#694e42] rounded-md font-medium"
                  >
                    Back to Home
                  </Link>
                </div>
              </div>
            </main>
            
            <Footer />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;